import { cn } from '@utils/index';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarColor, getInitials } from '@/utils/string';

/**
 * Size variants for UserAvatar component
 * Maps to common use cases throughout the app
 */
const avatarVariants = cva('', {
  variants: {
    size: {
      xs: 'h-4 w-4 text-[8px]', // 16px - Inline in text/dropdowns
      sm: 'h-6 w-6 text-[10px]', // 24px - Compact lists
      md: 'h-8 w-8 text-xs', // 32px - Default, tables, notes
      lg: 'h-10 w-10 text-sm', // 40px - Prominent displays, headers
      xl: 'h-12 w-12 text-base', // 48px - Profile pages
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const statusDotVariants: Record<string, string> = {
  xs: 'h-1.5 w-1.5 border',
  sm: 'h-2 w-2 border',
  md: 'h-2.5 w-2.5 border-2',
  lg: 'h-3 w-3 border-2',
  xl: 'h-3.5 w-3.5 border-2',
};

export interface UserAvatarProps
  extends React.ComponentPropsWithoutRef<typeof Avatar>, VariantProps<typeof avatarVariants> {
  /**
   * User's full name or display name
   * Used to generate initials
   */
  name?: string;

  /**
   * User's email address
   * Used as fallback if name is not provided
   */
  email?: string;

  /**
   * User's unique ID
   * Used for deterministic color assignment (stable across name changes)
   */
  userId?: string;

  /**
   * Optional custom fallback content
   * Overrides automatic initials generation
   */
  fallback?: React.ReactNode;

  /**
   * Availability status. When provided (not undefined), renders a status dot
   * on the bottom-right of the avatar (green = available, gray = not available).
   * Omit to render without a status dot.
   */
  available?: boolean;
}

/**
 * UserAvatar - A text-based avatar component for displaying user initials
 *
 * Features:
 * - Displays 1-2 letter initials from name (first + last initial)
 * - Deterministic color assignment based on name hash
 * - Multiple size variants (xs, sm, md, lg, xl)
 * - Optional availability status dot
 * - Graceful fallback for missing data (shows "?")
 * - Uses existing utility functions from @/utils/string
 *
 * @example
 * ```tsx
 * <UserAvatar name="John Doe" size="md" />
 * <UserAvatar name="Jane Doe" size="lg" available={true} />
 * ```
 */
export const UserAvatar = React.forwardRef<React.ElementRef<typeof Avatar>, UserAvatarProps>(
  ({ name, email, userId, fallback, size, available, className, ...props }, ref) => {
    // Determine display name: prefer name, fallback to email
    const displayName = name || email || '';

    if (!displayName && !fallback) {
      // If no name/email and no custom fallback, show "?" with default styling
      return null;
    }
    // Get initials and color using existing utility functions
    const initials = getInitials(displayName);
    // Use userId for color when available (stable across name changes), fallback to displayName
    const colorClasses = getAvatarColor(userId || displayName);

    const showStatus = available !== undefined;

    if (!showStatus) {
      return (
        <Avatar ref={ref} className={cn(avatarVariants({ size }), className)} {...props}>
          <AvatarFallback className={cn('font-medium', colorClasses)}>
            {fallback || initials}
          </AvatarFallback>
        </Avatar>
      );
    }

    const dotSize = statusDotVariants[size || 'md'];

    return (
      <div className="relative inline-flex">
        <Avatar ref={ref} className={cn(avatarVariants({ size }), className)} {...props}>
          <AvatarFallback className={cn('font-medium', colorClasses)}>
            {fallback || initials}
          </AvatarFallback>
        </Avatar>
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-background',
            dotSize,
            available ? 'bg-success' : 'bg-muted-foreground/40',
          )}
        />
      </div>
    );
  },
);

UserAvatar.displayName = 'UserAvatar';
